import {
    ChangeDetectionStrategy,
    Component,
    inject,
    output,
    computed,
    ViewEncapsulation,
    OnDestroy,
} from '@angular/core';
import { IOnboardingCompletionUserStep } from '../fi-onboarding-completion';
import {
    FiOnboardingCompletionStateType,
    FiOnboardingQuizCompletionStateType,
} from '../fi-onboarding-completion-enums';
import { ObjectTypes } from '@fi-shared/types/object-type';
import { FiOnboardingCompletionHelperService } from '../fi-onboarding-completion-helper.service';
import { FiOnboardingCompletionService } from '../fi-onboarding-completion.service';
import { FiOnboardingStepCompletionDialogState } from '../fi-onboarding-step-completion-dialog/fi-onboarding-step-completion-dialog-state.service';

@Component({
    selector: 'fi-onboarding-step-completion',
    templateUrl: './fi-onboarding-step-completion.component.html',
    styleUrl: './fi-onboarding-step-completion.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-onboarding-step-completion' },
    standalone: false,
})
export class FiOnboardingStepCompletionComponent implements OnDestroy {
    public readonly userCompletionFinished = output<void>();

    private readonly fiOnboardingCompletionHelperService = inject(FiOnboardingCompletionHelperService);
    private readonly fiOnboardingCompletionService = inject(FiOnboardingCompletionService);
    protected readonly completionDialogState = inject(FiOnboardingStepCompletionDialogState);

    private saveTempProgressTimeout: ReturnType<typeof setTimeout> | null = null;

    protected readonly isTimerHidden = computed(() => {
        const ucs = this.completionDialogState.userCompletionStep();
        return ucs.CompletionState !== FiOnboardingCompletionStateType.NotCompleted;
    });
    protected readonly isStepCompleted = computed(() => {
        const ucs = this.completionDialogState.userCompletionStep();
        return ucs.CompletionState === FiOnboardingCompletionStateType.Completed;
    });
    protected readonly isStepInProgress = computed(() => {
        const ucs = this.completionDialogState.userCompletionStep();
        return ucs.CompletionState === FiOnboardingCompletionStateType.InProgress;
    });
    private readonly isStepQuizAndNotCompleted = computed(() => {
        const ucs = this.completionDialogState.userCompletionStep();
        return (
            this.completionDialogState.stepCompletionConfig.isQuizStep &&
            ucs.CompletionState === FiOnboardingCompletionStateType.NotCompleted
        );
    });
    protected readonly isAllTodoItemsCompleted = computed(() => {
        const ucs = this.completionDialogState.userCompletionStep();
        return (
            this.completionDialogState.stepCompletionConfig.isTodoListStep &&
            !!ucs.TodoItemCompletions?.every((item) => item.IsCompleted)
        );
    });

    private readonly isDocumentStep = computed(
        () => this.completionDialogState.stepCompletionConfig.onboardingStep.ObjectType === ObjectTypes.Document,
    );
    private readonly isMediaBankStep = computed(
        () => this.completionDialogState.stepCompletionConfig.onboardingStep.ObjectType === ObjectTypes.MediaBank,
    );
    protected readonly isInfoBannerVisible = computed(
        () =>
            !this.completionDialogState.isReportMode() &&
            this.isStepCompleted() &&
            (this.isDocumentStep() || this.isMediaBankStep()),
    );

    protected onTodoUserCompletionStepChange(next: IOnboardingCompletionUserStep): void {
        this.completionDialogState.setUserCompletionStep(next);
        this.onTodoItemCompletionChanged();
    }

    protected updateStepCompletionState(completionState: FiOnboardingCompletionStateType): void {
        this.completionDialogState.updateUserCompletionStep((prev) => ({ ...prev, CompletionState: completionState }));
    }

    private updateQuizCompletionState(quizCompletionState: FiOnboardingQuizCompletionStateType): void {
        this.completionDialogState.updateUserCompletionStep((userCompletionStep) => ({
            ...userCompletionStep,
            QuizCompletion: {
                ...userCompletionStep.QuizCompletion,
                CompletionState: quizCompletionState,
            },
        }));
    }

    protected onTodoItemCompletionChanged(): void {
        this.saveTempProgress();
        this.completionDialogState.setSaveButtonHidden(false);
        this.completionDialogState.setCloseButtonHidden(true);
    }

    protected onQuizStarted(): void {
        this.saveTempProgress();
        this.completionDialogState.setSaveButtonHidden(false);
        this.completionDialogState.setSaveButtonDisabled(true);
    }

    protected onQuizTimeout(): void {
        this.completionDialogState.setSaveButtonHidden(true);
        this.completionDialogState.setSaveButtonDisabled(true);
        this.updateStepCompletionState(FiOnboardingCompletionStateType.Completed);
        this.saveCompletion(false);
    }

    //#region save completion
    public saveCompletion(completionFinished?: boolean): void {
        this.updateStepCompletionState(this.getStepCompletionState());
        this.saveCompletionInternal(completionFinished);
        if (this.isDocumentStep() || this.isMediaBankStep()) {
            this.completionDialogState.setSaveButtonHidden(true);
        }
    }

    private getStepCompletionState(): FiOnboardingCompletionStateType {
        /*const ucs = this.completionDialogState.userCompletionStep();

        if (this.completionDialogState.stepCompletionConfig.isQuizStep) {
            return ucs.CompletionState;
            return this.fiOnboardingCompletionHelperService.getStepCompletionStateQuiz(
                this.completionDialogState.stepCompletionConfig.onboardingStep.Quiz.AllowRetakes,
                ucs.QuizCompletion.CompletionState,
            );
        }*/

        if (this.completionDialogState.stepCompletionConfig.isTodoListStep) {
            return this.fiOnboardingCompletionHelperService.getStepCompletionStateTodoList(
                this.isAllTodoItemsCompleted(),
            );
        }

        return FiOnboardingCompletionStateType.Completed;
    }

    private shouldSkipSaveCompletion(): boolean {
        const ucs = this.completionDialogState.userCompletionStep();
        if (this.completionDialogState.stepCompletionConfig.isQuizStep) {
            return (
                this.fiOnboardingCompletionHelperService.isQuizCompletedOfFailed(ucs.QuizCompletion?.CompletionState) ||
                !this.fiOnboardingCompletionHelperService.isQuizStepCompletionInProgress(ucs)
            );
        }

        return ucs.CompletionState === FiOnboardingCompletionStateType.Completed;
    }
    public saveTempProgress(completionFinished: boolean = false): void {
        const skipSaveCompletion = this.shouldSkipSaveCompletion();
        if (skipSaveCompletion) {
            this.userCompletionFinished.emit();
            return;
        }

        if (this.saveTempProgressTimeout !== null) {
            clearTimeout(this.saveTempProgressTimeout);
        }

        this.saveTempProgressTimeout = setTimeout(() => {
            this.saveCompletionInternal(completionFinished);
            this.saveTempProgressTimeout = null;
        }, 300);
    }

    private saveCompletionInternal(completionFinished = true): void {
        if (this.isStepQuizAndNotCompleted()) {
            this.userCompletionFinished.emit();
            return;
        }

        // note: we should add cancel request feature in case if we need to save udpated data before the response is received
        this.fiOnboardingCompletionService
            .saveOnboardingCompletion(
                this.fiOnboardingCompletionHelperService.getOnboardingCompletionSaveModel({
                    onboardingId: this.completionDialogState.stepCompletionConfig.onboardingId,
                    userId: this.completionDialogState.stepCompletionConfig.userId,
                    onboardingStep: this.completionDialogState.stepCompletionConfig.onboardingStep,
                    userCompletionStep: this.completionDialogState.userCompletionStep(),
                }),
            )
            .then((saveResult) => {
                this.fiOnboardingCompletionHelperService.updateOnboardingCompletionSignalFromSaveResult(
                    this.completionDialogState.userCompletionStep,
                    saveResult,
                    (newCompletionState) => this.updateStepCompletionState(newCompletionState),
                    (newQuizCompletionState) => this.updateQuizCompletionState(newQuizCompletionState),
                );
                this.handleSaveCompletionResponse(completionFinished);
            });
    }

    private handleSaveCompletionResponse(completionFinished: boolean): void {
        if (!completionFinished) {
            return;
        }
        if (this.shouldKeepDialogOpenAfterSave()) {
            this.completionDialogState.setSaveButtonHidden(true);
            return;
        }
        this.userCompletionFinished.emit();
    }

    private shouldKeepDialogOpenAfterSave(): boolean {
        const ucs = this.completionDialogState.userCompletionStep();
        return this.fiOnboardingCompletionHelperService.shouldKeepDialogOpenAfterSave(
            this.completionDialogState.stepCompletionConfig.onboardingStep.ObjectType,
            ucs.QuizCompletion?.CompletionState,
        );
    }
    //#endregion

    public ngOnDestroy(): void {
        if (this.saveTempProgressTimeout !== null) {
            clearTimeout(this.saveTempProgressTimeout);
            this.saveTempProgressTimeout = null;
        }
    }

    protected readonly FiOnboardingCompletionStateType = FiOnboardingCompletionStateType;
    protected readonly ObjectTypes = ObjectTypes;
}
